/**
 * @file RepDocCard.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-30
 * @description One record — an invoice, a purchase order, a standing order —
 *              as a card on a rep's phone. Cam, 28 Oct 2026 (rep mobile
 *              review, "All 20"): the invoice, PO and standing order cards
 *              were the desktop table squeezed by CSS — tiny 9–10px labels,
 *              a Held badge laid over long names, and a tap that only
 *              highlighted the row. They are now built the way the Sales
 *              Orders card is (RepOrderCard), from the same pieces and the
 *              same stylesheet.
 *
 * The rules are the order card's:
 *  - everything sits in normal flow, so nothing can sit on anything else;
 *  - the card itself is ONE button that opens the record (the chevron says
 *    so);
 *  - anything else to tap is its own labelled button along the foot, at
 *    least 44px high;
 *  - status in words (utils/repDocCards), never hover-only icons.
 *
 * Read only, like everything a rep sees.
 *
 * 1.1.0 (2026-10-30): `badge` — an account number as the account badge
 * before the title, the way the customer window, My contracts and My
 * pricing show a customer. The customers list (RepContactCard) uses it.
 */

import React from 'react';
import './RepOrderCard.css';

function RepDocCard({
  number,
  kind = '',
  badge = '',
  title,
  place = '',
  when = null,
  flags = [],
  chip = null,
  extraChips = [],
  total = '',
  totalSub = '',
  archived = false,
  onOpen,
  openLabel,
  children,
  actions = []
}) {
  return (
    <article className={`rep-order-card rep-doc-card${archived ? ' is-archived' : ''}`}>
      <button
        type="button"
        data-read-safe
        className="roc-main rdc-main"
        onClick={onOpen}
        aria-label={openLabel || `Open ${number || title}`}
      >
        <span className="roc-head">
          <span className="roc-who">
            {(number || kind) && (
              <span className="roc-number">
                {number}
                {kind && <span className="rdc-kind">{number ? ` · ${kind}` : kind}</span>}
              </span>
            )}
            {badge ? (
              <span className="roc-customer rdc-title">
                <span className="account-badge-inline rdc-badge">{badge}</span>
                <span className="rdc-title-text">{title}</span>
              </span>
            ) : (
              <span className="roc-customer">{title}</span>
            )}
            {place && <span className="roc-place">{place}</span>}
          </span>
          {when && (
            <span className="roc-when">
              <span className={`roc-when-label${when.near ? ' is-near' : ''}${when.alert ? ' is-alert' : ''}`}>
                {when.nearLabel && when.near ? `${when.label} · ${when.nearLabel}` : when.label}
              </span>
              <span className="roc-when-date">{when.text}</span>
            </span>
          )}
          <span className="material-symbols-rounded roc-chevron roc-open" aria-hidden="true">chevron_right</span>
        </span>

        {flags.length > 0 && (
          <span className="roc-flags">
            {flags.map(f => <span key={f.key} className="roc-flag">{f.label}</span>)}
          </span>
        )}

        {children}

        {(chip || extraChips.length > 0 || total) && (
          <span className="roc-status">
            {chip && <span className={`roc-chip tone-${chip.tone || 'muted'}`}>{chip.label}</span>}
            {extraChips.map(c => (
              <span key={c.key || c.label} className={`roc-chip tone-${c.tone || 'muted'}`}>{c.label}</span>
            ))}
            {total && (
              <span className="roc-total rdc-total">
                {total}
                {totalSub && <span className="rdc-total-sub">{totalSub}</span>}
              </span>
            )}
          </span>
        )}
      </button>

      {actions.length > 0 && (
        <div className="roc-actions">
          {actions.map(a => (
            <button
              key={a.key}
              type="button"
              data-read-safe
              className="roc-action"
              onClick={a.onClick || undefined}
              disabled={!!a.disabled || !a.onClick}
              title={a.disabled || !a.onClick ? (a.disabledTitle || undefined) : a.title}
            >
              {a.icon && <span className="material-symbols-rounded" aria-hidden="true">{a.icon}</span>}
              {a.label}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

/** The list a page of cards sits in, with its empty and loading states. */
export function RepDocCards({ loading, empty = 'Nothing found', count, children }) {
  return (
    <div className="rep-order-cards rep-doc-cards">
      {loading ? (
        <div className="rep-order-cards-empty">Loading…</div>
      ) : count === 0 ? (
        <div className="rep-order-cards-empty">{empty}</div>
      ) : children}
    </div>
  );
}

export default RepDocCard;
